import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, Work } from '@mui/icons-material';
import { FC, useState } from 'react';

import { useAuth } from '@carcareers/hooks';
import { useJobseekerDeleteExperienceMutation, useJobseekerExperiencesQuery } from '@carcareers/store';
import { JobseekerExperienceModal } from '@carcareers/components';
import { JobseekerExperience } from '@libs/models';

export const ExperienceCard: FC = () => {
  const { session } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editExperience, setEditExperience] = useState<JobseekerExperience | null>(null);

  const [deleteExperience, { isLoading: isDeleting }] = useJobseekerDeleteExperienceMutation();
  const { data: experiences } = useJobseekerExperiencesQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const handleOpenAdd = () => setIsAddOpen(true);
  const handleCloseAdd = () => setIsAddOpen(false);

  const handleEditExperience = (experience: JobseekerExperience) => {
    setEditExperience(experience);
  };

  const handleCloseEdit = () => {
    setEditExperience(null);
  };

  const handleDeleteExperience = (experienceId: number) => {
    const jobseekerId = session?.profile?.id ?? 0;
    deleteExperience({ jobseekerId, experienceId });
  };

  function formatMonthYear(date?: Date | null): string {
    if (!date) return '';
    const newDate = new Date(date);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', timeZone: 'UTC' };
    return new Intl.DateTimeFormat('en-US', options).format(newDate);
  }

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
              <Work sx={{ mr: 1 }} />
              Experience
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'flex' },
              }}
              onClick={handleOpenAdd}
            >
              Add Experience
            </Button>
          </Box>

          <List
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                position: 'unset',
                top: '32px',
                transform: 'unset',
              },
            }}
          >
            {experiences?.map((experience, index) => (
              <Box key={experience.id}>
                <ListItem
                  sx={{ px: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditExperience(experience)}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteExperience(experience.id)}
                      >
                        {isDeleting ? <CircularProgress size={18} color="secondary" /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={experience.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="primary">
                          {experience.company}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMonthYear(experience.startDate)} -{' '}
                          {experience.current ? 'Present' : formatMonthYear(experience.endDate)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '1.5em',
                              background: 'linear-gradient(transparent, white)',
                              pointerEvents: 'none',
                            },
                          }}
                        >
                          {experience.description}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (experiences?.length ?? 0) - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            sx={{
              borderRadius: 2,
              height: 32,
              width: '100%',
              mt: 2,
              display: { xs: 'flex', md: 'none' },
            }}
            onClick={handleOpenAdd}
          >
            Add Experience
          </Button>
        </CardContent>
      </Card>

      <JobseekerExperienceModal type="add" open={isAddOpen} onClose={handleCloseAdd} />

      {editExperience && (
        <JobseekerExperienceModal
          type="edit"
          open={!!editExperience}
          onClose={handleCloseEdit}
          experience={editExperience}
        />
      )}
    </>
  );
};
